import { brandMarkLogo, DEFAULT_OG_IMAGE, SITE_ORIGIN } from "./siteOrigin";
import { tournamentEventJsonLd } from "../../prerender/tournamentJsonLd";
import { HOME_DESCRIPTION } from "../../utility/aiAssistCopy";
import { PLAN_TIERS, planFeatureText } from "../../utility/planTiers";

export const generateTournamentStructuredData = (tournament) =>
  tournamentEventJsonLd(tournament);

export const generateOrganizationStructuredData = (organization) => {
  if (!organization) return null;

  const baseUrl = SITE_ORIGIN;

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: organization.name,
    description:
      organization.description || `Bowling organization: ${organization.name}`,
    url: `${baseUrl}/organization`,
    ...(organization.logo ? { logo: `${baseUrl}${organization.logo}` } : {}),
    contactPoint: organization.contactEmail
      ? {
          "@type": "ContactPoint",
          email: organization.contactEmail,
          contactType: "customer service",
        }
      : undefined,
    address: organization.address
      ? {
          "@type": "PostalAddress",
          streetAddress: organization.address,
          addressLocality: organization.city,
          addressRegion: organization.state,
          postalCode: organization.zipCode,
          addressCountry: "US",
        }
      : undefined,
    sameAs: organization.website ? [organization.website] : undefined,
  };
};

export const generateBowlingCenterStructuredData = (center) => {
  if (!center) return null;

  const baseUrl = SITE_ORIGIN;

  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${baseUrl}/bowling-center/${center.id}`,
    name: center.centerName,
    description: center.description || `Bowling center: ${center.centerName}`,
    url: center.website || `${baseUrl}/bowling-center/${center.id}`,
    image: center.image
      ? `${baseUrl}${center.image}`
      : `${baseUrl}${DEFAULT_OG_IMAGE}`,
    address: {
      "@type": "PostalAddress",
      streetAddress: center.formattedAddress || center.address,
      addressLocality: center.city,
      addressRegion: center.state,
      postalCode: center.zipCode,
      addressCountry: "US",
    },
    geo:
      center.latitude && center.longitude
        ? {
            "@type": "GeoCoordinates",
            latitude: center.latitude,
            longitude: center.longitude,
          }
        : undefined,
    telephone: center.phone,
    email: center.email,
    openingHours: center.hours
      ? center.hours.split(",").map((day) => day.trim())
      : undefined,
    priceRange: center.priceRange || "$$",
    amenityFeature: [
      {
        "@type": "LocationFeatureSpecification",
        name: "Bowling Lanes",
        value: center.laneCount
          ? `${center.laneCount} lanes`
          : "Multiple lanes",
      },
    ],
    aggregateRating: center.rating
      ? {
          "@type": "AggregateRating",
          ratingValue: center.rating,
          reviewCount: center.reviewCount || 1,
        }
      : undefined,
  };
};

export const generateWebsiteStructuredData = () => {
  const baseUrl = SITE_ORIGIN;

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        name: "Bowling Tournament App",
        alternateName: "BTA",
        description: HOME_DESCRIPTION,
        url: baseUrl,
        publisher: {
          "@id": `${baseUrl}/#organization`,
        },
      },
      {
        "@type": "Organization",
        "@id": `${baseUrl}/#organization`,
        name: "Bowling Tournament App",
        url: baseUrl,
        logo: brandMarkLogo(baseUrl),
      },
      {
        "@type": "SoftwareApplication",
        name: "Bowling Tournament App",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        url: `${baseUrl}/bowling-tournament-software`,
        image: `${baseUrl}${DEFAULT_OG_IMAGE}`,
        description: HOME_DESCRIPTION,
        featureList: [
          "AI average import",
          "AI signup sheet import",
          "AI recap sheet import",
          "Tournament registration",
          "Waitlist",
          "Squad management",
          "Scoring",
          "Live standings",
          "Card payments",
          "Prize funds",
          "Side pots",
          "Payouts",
        ],
        publisher: {
          "@id": `${baseUrl}/#organization`,
        },
      },
    ],
  };
};

/**
 * Generate WebPage structured data for policy/legal pages
 */
export const generateWebPageStructuredData = ({
  name,
  description,
  url,
  datePublished,
  dateModified,
  author = "Bowling Tournament App",
}) => {
  const baseUrl = SITE_ORIGIN;
  const path =
    url || (typeof window !== "undefined" ? window.location.pathname : "/");
  const fullUrl = `${baseUrl}${path}`;

  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: name,
    description: description,
    url: fullUrl,
    inLanguage: "en-US",
    isPartOf: {
      "@type": "WebSite",
      name: "Bowling Tournament App",
      alternateName: "BTA",
      url: baseUrl,
    },
    publisher: {
      "@type": "Organization",
      name: "Bowling Tournament App",
      url: baseUrl,
      logo: brandMarkLogo(baseUrl),
    },
    ...(datePublished && { datePublished }),
    ...(dateModified && { dateModified }),
    ...(author && {
      author: {
        "@type": "Organization",
        name: author,
      },
    }),
  };
};

const offerPrice = (price) => String(price || "").replace(/[^0-9.]/g, "") || "0";

/**
 * Plans page: one WebPage whose main entity lists Free, AI Assist, and Priority.
 */
export const generatePlansStructuredData = ({
  name = "Plans",
  description,
  url = "/pricing",
  dateModified = "2026-10-28",
} = {}) => {
  const page = generateWebPageStructuredData({
    name,
    description,
    url,
    dateModified,
  });
  const fullUrl = `${SITE_ORIGIN}${url}`;
  page.mainEntity = {
    "@type": "ItemList",
    name: "Plans",
    numberOfItems: PLAN_TIERS.length,
    itemListElement: PLAN_TIERS.map((tier, index) => {
      const price = offerPrice(tier.price);
      const offer = {
        "@type": "Offer",
        name: tier.name,
        description: [...(tier.notes || []), ...(tier.points || [])]
          .map(planFeatureText)
          .filter(Boolean)
          .join(" "),
        price,
        priceCurrency: "USD",
        url: fullUrl,
        availability: "https://schema.org/InStock",
      };
      if (tier.period) {
        offer.priceSpecification = {
          "@type": "UnitPriceSpecification",
          price,
          priceCurrency: "USD",
          billingDuration: "P1Y",
          unitText: "year",
        };
      }
      return {
        "@type": "ListItem",
        position: index + 1,
        name: tier.name,
        item: offer,
      };
    }),
  };
  return page;
};

/**
 * Generate BreadcrumbList structured data
 */
export const generateBreadcrumbStructuredData = (breadcrumbs) => {
  if (!breadcrumbs || breadcrumbs.length === 0) return null;

  const baseUrl = SITE_ORIGIN;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.label,
      item: item.path ? `${baseUrl}${item.path}` : undefined,
    })),
  };
};
